import {CommandCaller} from "../../lib/commands";
import * as Immutable from 'immutable';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as _ from 'lodash';

import {Loading} from "../../react-components/Loading";
import {Person, PersonJson} from "../../contacts/value objects";
import {Template} from "../../innertemplatemanager/Template";
import {TemplateableComponent, TemplateableComponentProps} from "../../innertemplatemanager/TemplateableComponent";

import {SeparateFilterPlaceholder, GroupedFilterPlaceholder} from "./filter/FilterPlaceholder";

import {ResultsPlaceholder, ResultsGroupedByCategoriesPlaceholder} from "./results/ResultsPlaceholder";
import {CallJs, DangerousHtmlString} from "../../lib/types";

import {
	FieldFilter, FieldFilterJson, ArchiveFilter, KeywordFilter, CategoryFilter, FilterSelectionState,
	ArchivedStatus, Filter, OptionFilter
} from "./filter/ValueObjects";

import {ArticleSearchResult, ArticleSearchResultJson} from "./results/ValueObjects";
import {Category, CategoryTreeJson, OptionValue, Preferences, PreferencesJson, ReadableLabels, Config} from "./ValueObjects";
import {Form as PreferencesForm} from './preferences/PreferencesForm'
import {SerializeableAppState} from './types'

import {createHashHistory} from 'history'
import * as History from "history"
import {StateSerializer} from "./state serializer"

interface StringMap<T> {
	[index: string]:T;
}

interface InitAdvancedSearchData {
	advancedSearchPagePlaceholders:StringMap<string>
	defaultSearchResults:{articles:ArticleSearchResultJson[], totalResults:string}
}

interface AdvancedSearchParams {
	fieldFilters:FieldFilterJson[]
	searchResultTemplate:string
	advancedSearchTemplate:string
	categoriesTree:CategoryTreeJson[]
	preferences:PreferencesJson
	user:PersonJson
	preferencesFormHeader:string
	ajaxPageUrl:string
	canSendDetailsToUser:boolean
	config:Config
	labels:ReadableLabels
}

export const advancedSearchTemplate:CallJs<AdvancedSearchParams> =  async (replace, params) => {
	const user = params.user ? Person.fromObject(params.user) : null
	const categoriesTree = Category.fromObjects(params.categoriesTree);
	const categoriesList:Category[] = Category.flattenTree(categoriesTree);

	const advancedSearchTemplate = Template.fromString(params.advancedSearchTemplate)
	const searchResultTemplate = Template.fromString(params.searchResultTemplate)

	const node = document.createElement('div')
	replace(node)

	ReactDOM.render(<Loading className="loading-advanced-search"/>, node)
	
	const initialData:InitAdvancedSearchData = await new CommandCaller(params.ajaxPageUrl).runGet('init advanced search', {
		advancedSearchPagePlaceholders: advancedSearchTemplate.getKnownPlaceholders(),
		searchResultItemPlaceholders: searchResultTemplate.getKnownPlaceholders()
	}) as InitAdvancedSearchData

	ReactDOM.render(
		<AdvancedSearchApp
			fieldFilters={FieldFilter.fromObjects(params.fieldFilters)}
			archiveFilter={params.config.archiveEnabled ? new ArchiveFilter() : null}
			keywordFilter={params.config.keywordsEnabled ? new KeywordFilter() : null}
			categoryFilter={params.config.categoriesEnabled ? new CategoryFilter(categoriesTree) : null}
			categoriesTree={categoriesTree}
			categoriesList={categoriesList}
			template={advancedSearchTemplate}
			templatePlaceholders={initialData.advancedSearchPagePlaceholders}
			searchResultTemplate={searchResultTemplate}
			defaultArticleResults={ArticleSearchResult.fromObjects(initialData.defaultSearchResults.articles, categoriesList)}
			defaultTotalResults={parseInt(initialData.defaultSearchResults.totalResults)}
		    preferences={params.preferences === null ? null : Preferences.fromObject(params.preferences)}
			preferencesFormHeader={params.preferencesFormHeader}
		    user={user}
			ajaxPageUrl={params.ajaxPageUrl}
			canSendDetailsToUser={params.canSendDetailsToUser}
			config={params.config}
			labels={params.labels}
		/>,
		node
	);
}

export interface AdvancedSearchAppProps extends AdvancedSearchProps {
	preferencesFormHeader:DangerousHtmlString
}

export class AdvancedSearchAppState implements SerializeableAppState {
	preferences:Preferences|null = null
	groupByCategories:boolean = false
	openPreferences:boolean = false

	searchResults:ArticleSearchResult[] = []
	filterSelections:FilterSelectionState = new FilterSelectionState()
	expandedFilters:Map<Filter, boolean>  = new Map<Filter, boolean>()

	keywords:string = ''

	currentPage:number = 0
	totalResults:number = 0

	currentSearch:ArticleSearcher|null = null
}

class AdvancedSearchApp extends React.Component<AdvancedSearchAppProps, AdvancedSearchAppState> {

	private history:History.History
	private currentUrl:string|undefined

	constructor(props:AdvancedSearchAppProps, context?:any) {
		super(props, context)

		this.history = createHashHistory()

		const state = new AdvancedSearchAppState()
		state.searchResults = props.defaultArticleResults

		if (props.categoryFilter !== null) {
			state.expandedFilters.set(props.categoryFilter, true)
		}

		if (props.archiveFilter !== null) {
			state.filterSelections.setSelected(props.archiveFilter, ArchivedStatus.CURRENT, true)
		}

		state.totalResults = props.defaultTotalResults
		state.currentPage = 0
		state.keywords = ""
		state.preferences = props.preferences
		state.openPreferences = props.preferences == null && props.user != null

		const defaultCategory = this.defaultCategory()
		if (defaultCategory !== null && props.categoryFilter !== null) {
			state.filterSelections.setSelected(props.categoryFilter, defaultCategory, true)
			if (defaultCategory == Category.PREFERRED) {
				state.groupByCategories = true
			}
		}

		this.state = state
	}

	private defaultCategory():Category|null {
		if (this.props.preferences != null && this.props.preferences.categoryIds.size > 0) {
			return Category.PREFERRED
		} else if (this.props.config.defaultCategoryId > 0) {
			const category = this.props.categoriesList.find((cat) => cat.getNumericId() == this.props.config.defaultCategoryId)
			if (category != null) {
				return category
			}
		}

		return null
	}

	componentDidMount() {
		this.history.listen((location:History.Location) => {
			const newState = new StateSerializer(this.state, this.props.categoryFilter, this.props.archiveFilter, this.props.fieldFilters, this.defaultCategory(), this.props.config).deserialize(location)
			if (newState != this.state) {
				this.currentUrl = location.search
				this.setState(newState, this.runNormalSearch)
			}
		})
	}

	render() {

		const onShowPreferences = (show:boolean) => {
			this.setState((prevState) => {
				prevState.openPreferences = show
				return prevState
			})
		}

		return <div>
			<PreferencesForm
				preferences={this.state.preferences}
				user={this.props.user}
				categoryTree={this.props.categoriesTree}
				isShown={this.state.openPreferences}
				hide={() => onShowPreferences(false)}
				header={this.props.preferencesFormHeader}
				saveCategories={(categories:Category[]) => this.savePreferences(categories)}
			/>
			<SearchPage
				{...this.props}
				placeholders={this.props.templatePlaceholders}
				preferences={this.state.preferences}
				groupByCategories={this.state.groupByCategories}
				currentPage={this.state.currentPage}
				expandedFilters={this.state.expandedFilters}
				filterSelections={this.state.filterSelections}
				keywords={this.state.keywords}
				totalResults={this.state.totalResults}
				searchResults={this.state.searchResults}
				onFilterExpanded={() => {}}
				onKeywordsChanged={(keywords) => this.onKeywordsChanged(keywords)}
				onFilterCleared={(filter) => this.onFilterCleared(filter)}
				onSelectPage={(pageNum) => this.onSelectPage(pageNum)}
				onRunSearch={() => this.runNormalSearch()}
				onFilterChanged={(filter, value, isSelected) => this.onFilterChanged(filter, value, isSelected)}
				showPreferences={onShowPreferences}
				canSendDetailsToUser={this.props.canSendDetailsToUser}
				isLoading={this.state.currentSearch != null}
			/>
		</div>
	}

	private debouncedHistoryPush = _.debounce((path:string) => this.history.push(path), 1000)

	private setStateAndUpdateUrl(stateSetter:(prevState:AdvancedSearchAppState) => AdvancedSearchAppState, callback?:() => void) {
		this.setState(stateSetter, () => {
			const serialized = new StateSerializer(this.state, this.props.categoryFilter, this.props.archiveFilter, this.props.fieldFilters, this.defaultCategory(), this.props.config).serialize()
			if (serialized != this.currentUrl) {
				this.debouncedHistoryPush(serialized)
			}

			if (callback != null) {
				callback()
			}
		})
	}

	private async savePreferences(categories:Category[]) {
		const caller = new CommandCaller(this.props.ajaxPageUrl)
		const preferencesObject = await caller.runPost('save search preferences', {
			categoryIds: categories.map((category) => category.getId())
		}) as PreferencesJson

		this.setState(
			(prevState) => {
				prevState.preferences = Preferences.fromObject(preferencesObject)

				if (this.props.categoryFilter !== null) {
					prevState.filterSelections.setSelected(this.props.categoryFilter, Category.PREFERRED, true)
				}

				return prevState
			},
			() => {
				const searcher = this.createSearcher()
				searcher.groupByCategories = true
				searcher.categoriesToFilterOn = this.state.preferences === null ? [] : this.state.preferences.categoryIds.toArray()
				searcher.pageToFetch = 0
				this.runSearch(searcher)
			}
		)
	}

	private onSelectPage(page:number) {
		this.setStateAndUpdateUrl(
			(prevState) => {
				prevState.currentPage = page;
				return prevState;
			}, this.runNormalSearch
		)
	}

	private onFilterChanged(filter:OptionFilter<OptionValue>, value:OptionValue, isSelected:boolean) {
		this.setStateAndUpdateUrl(
			(prevState) => {
				prevState.filterSelections.setSelected(filter, value, isSelected);
				prevState.currentPage = 0;
				if (filter.autoCloseOnSelection()) {
					prevState.expandedFilters.set(filter, false);
				}
				return prevState;
			}, this.runNormalSearch
		);
	}

	private onFilterCleared(filter:OptionFilter<OptionValue>) {
		this.setStateAndUpdateUrl(
			(prevState) => {
				prevState.filterSelections.clearSelected(filter);
				prevState.currentPage = 0;
				if (filter.autoCloseOnSelection()) {
					prevState.expandedFilters.set(filter, false);
				}
				return prevState;
			}, this.runNormalSearch
		);
	}

	private onKeywordsChanged(keywords:string) {
		const doSearch = () => {
			const searcher = this.createSearcher()
			searcher.keywords = keywords
			searcher.pageToFetch = 0
			this.runSearch(searcher)
		}

		this.setStateAndUpdateUrl((prevState) => {
			prevState.keywords = keywords
			return prevState
		}, doSearch)
	}

	// Run a search based on the current state (i.e. current page, current filters, etc.
	private runNormalSearch = () => {
		this.runSearch(this.createSearcher())
	}

	private async runSearch(searcher:ArticleSearcher):Promise<void> {
		this.runDebouncedSearch(searcher)
	}


	/**
	 * Do a search after 500ms. Each call to this function will reset the counter again.
	 */
	private runDebouncedSearch:(searcher:ArticleSearcher) => Promise<void> = _.debounce(async (searcher:ArticleSearcher) => {

		this.setState((prevState) => {
			prevState.currentSearch = searcher
			return prevState
		});

		try {
			const result = await searcher.run()

			if (searcher != this.state.currentSearch) {
				return
			}

			// Ensure that the particular parameters that were sent to the server are reflected in the UI.
			this.setStateAndUpdateUrl((prevState) => {

				prevState.searchResults = (this.props.config.resultsDisplay == "infiniteScrolling" && result.pageFetched != 0)
					? prevState.searchResults.concat(result.searchResults)
					: result.searchResults

				prevState.totalResults      = result.totalResults
				prevState.currentPage       = result.pageFetched
				prevState.groupByCategories = result.groupByCategories
				prevState.currentSearch     = null
				return prevState;
			})
		} catch (error) {

			if (searcher != this.state.currentSearch) {
				return
			}

			this.setStateAndUpdateUrl((prevState) => {
				prevState.searchResults     = []
				prevState.totalResults      = 0
				prevState.currentPage       = 0
				prevState.groupByCategories = searcher.groupByCategories
				prevState.currentSearch     = null
				return prevState
			})

		}
		return //this silences a compiler error.
	}, 500)

	private createSearcher() {
		const {categoryFilter} = this.props
		const preferredCategoriesSelected = categoryFilter !== null ? this.state.filterSelections.getSelected(categoryFilter).has(Category.PREFERRED) : false

		return new ArticleSearcher(
			this.props.ajaxPageUrl,
			this.state.currentPage,
			this.state.keywords,
			this.state.filterSelections,
			this.props.searchResultTemplate.getKnownPlaceholders(),
			preferredCategoriesSelected,
			preferredCategoriesSelected
				? (this.state.preferences === null ? [] : this.state.preferences.categoryIds.toArray())
				: (categoryFilter === null ? [] : this.state.filterSelections.serializeFilter(categoryFilter)),
			this.props.archiveFilter,
			this.props.categoriesList
		)
	}

}

// Encodes not only the result of the search (i.e. searchResults and totalResults), but also
// the parameters of interest used to perform the search, which can be used to update the UI in
// response to the search being completed.
class ArticleSearcherResponse {
	constructor(
		private searcher:ArticleSearcher,
		private _searchResults:ArticleSearchResult[],
		private _totalResults:number,
		public pageFetched:number
	) {}

	get searchResults() { return this._searchResults }
	get totalResults() { return this._totalResults }
	get groupByCategories() { return this.searcher.groupByCategories }
	get pageToFetch() { return this.searcher.pageToFetch }
	get keywords() { return this.searcher.keywords }
}

class ArticleSearcher {

	constructor(
		private ajaxPageUrl:string,
		public pageToFetch:number,
		public keywords:string,
		private filterSelections:FilterSelectionState,
		private placeholdersToReturn:string[],
		public groupByCategories:boolean,
		public categoriesToFilterOn:string[]|number[],
		private archiveFilter:ArchiveFilter|null,
		private categoriesList:Category[]
	) {}

	async run():Promise<ArticleSearcherResponse> {
		const params = {
			keywords:                this.keywords,
			fieldFilters:            this.filterSelections.serialize(),
			selectedCategories:      this.categoriesToFilterOn,
			selectedArchiveStatuses: this.archiveFilter === null ? [] : this.filterSelections.serializeFilter(this.archiveFilter),
			placeholdersToReturn:    this.placeholdersToReturn,
			currentPage:             this.pageToFetch,
			groupByCategories:       this.groupByCategories,
		}

		const data = await new CommandCaller(this.ajaxPageUrl).runGet('public.search', params) as SearchResults

		return new ArticleSearcherResponse(
			this,
			ArticleSearchResult.fromObjects(data.articles, this.categoriesList),
			data.totalResults,
			data.pageNum
		)
	}
}

export interface AdvancedSearchProps {
	template:Template
	templatePlaceholders:StringMap<string>
	fieldFilters:FieldFilter[]
	categoryFilter:CategoryFilter|null
	archiveFilter:ArchiveFilter|null
	keywordFilter:KeywordFilter|null
	categoriesTree:Category[]
	categoriesList:Category[]
	searchResultTemplate:Template
	defaultArticleResults:ArticleSearchResult[]
	defaultTotalResults:number
	preferences:Preferences|null
	user:Person|null
	ajaxPageUrl:string
	canSendDetailsToUser:boolean
	labels:ReadableLabels
	config:Config
}

interface SearchPageProps extends AdvancedSearchProps, TemplateableComponentProps {
	showPreferences:(show:boolean) => void
	groupByCategories:boolean

	onKeywordsChanged:(keywords:string) => void
	onFilterExpanded:(filter:Filter, isExpanded:boolean) => void
	onFilterChanged:(filter:OptionFilter<OptionValue>, value:OptionValue, isSelected:boolean) => void
	onFilterCleared:(filter:OptionFilter<OptionValue>) => void
	onSelectPage:(pageNum:number) => void
	onRunSearch:() => void

	searchResults:ArticleSearchResult[]
	filterSelections:FilterSelectionState
	expandedFilters:Map<Filter, boolean>

	keywords:string

	currentPage:number
	totalResults:number

	canSendDetailsToUser:boolean
	isLoading:boolean
}

class SearchPage extends TemplateableComponent<SearchPageProps, {}> {

	private static get ATTR_SHOW_PREFERENCE_FORM():string { return 'data-show-preferences-form' }

	protected getStaticPlaceholders():PlaceholderCollection {
		return {
			"PreferencesForm:Show:Attribute": this.props.user != null ? SearchPage.ATTR_SHOW_PREFERENCE_FORM : ''
		}
	}

	protected renderPlaceholders():StringMap<React.ReactElement<any>> {
		const categoriesTreeWithPreferred = (this.props.user == null ? [] : [Category.PREFERRED]).concat(this.props.categoriesTree)

		return {
			"Filter:Separate":
				<SeparateFilterPlaceholder
					fieldFilters={this.props.fieldFilters}
					archiveFilter={this.props.archiveFilter}
					keywordFilter={this.props.keywordFilter}
					categoryFilter={this.props.categoryFilter}
					categories={categoriesTreeWithPreferred}
					keywordsChanged={this.props.onKeywordsChanged}
					filterSelectionChanged={this.props.onFilterChanged}
					filterSelectionCleared={this.props.onFilterCleared}
					optionFilterSelections={this.props.filterSelections}
					keywords={this.props.keywords}
					runSearch={this.props.onRunSearch}
					labels={this.props.labels}
				/>,
			"Filter:Grouped":
				<GroupedFilterPlaceholder
					fieldFilters={this.props.fieldFilters}
					archiveFilter={this.props.archiveFilter}
					keywordFilter={this.props.keywordFilter}
					categoryFilter={this.props.categoryFilter}
					categories={categoriesTreeWithPreferred}
					keywordsChanged={this.props.onKeywordsChanged}
					filterSelectionChanged={this.props.onFilterChanged}
					filterSelectionCleared={this.props.onFilterCleared}
					optionFilterSelections={this.props.filterSelections}
					keywords={this.props.keywords}
					runSearch={this.props.onRunSearch}
					labels={this.props.labels}
				/>,
			"Results:List": this.resultsPlaceholder()
		};
	}

	componentDidMount() {
		super.componentDidMount()
		const node = ReactDOM.findDOMNode(this)
		const found = $(`[${SearchPage.ATTR_SHOW_PREFERENCE_FORM}]`)
		found.click(() => {
			this.props.showPreferences(true)
		})
	}

	private resultsPlaceholder():JSX.Element {
		const paginatorProps = {
			numPages: this.props.totalResults / this.props.config.resultsPerPage,
			currentPage: this.props.currentPage,
			onSelectPage: this.props.onSelectPage,
			isLoading : this.props.isLoading,
		}

		return this.props.groupByCategories
			? <ResultsGroupedByCategoriesPlaceholder
				paginatorProps={paginatorProps}
				results={this.props.searchResults}
				template={this.props.searchResultTemplate}
				keywords={this.props.keywords}
				categories={this.props.categoriesList}
				categoryIdsToHighlight={this.props.preferences === null ? Immutable.Set<number>() : this.props.preferences.categoryIds}
				ajaxPageUrl={this.props.ajaxPageUrl}
				canSendDetailsToUser={this.props.canSendDetailsToUser}
				isLoading={this.props.isLoading}
				config={this.props.config}
			/>
			: <ResultsPlaceholder
				paginatorProps={paginatorProps}
				results={this.props.searchResults}
				template={this.props.searchResultTemplate}
				keywords={this.props.keywords}
				ajaxPageUrl={this.props.ajaxPageUrl}
				canSendDetailsToUser={this.props.canSendDetailsToUser}
				isLoading={this.props.isLoading}
				config={this.props.config}
			/>
	}
}

interface SearchResults {
	articles:ArticleSearchResultJson[]
	totalResults:number
	pageNum:number
}



// WEBPACK FOOTER //
// ./src/news2/advancedSearch/AdvancedSearch.tsx